import { useCallback, useRef, useState } from 'react';
import {
    buildDownloadsClickExtraData,
    useTracker,
} from '@archipro-website/tracker';
import { useLocalStorageState } from 'ahooks';
import { DownloadSurface } from 'generated/graphql';

import { useUser, isUserLoggedIn } from '~/modules/user';
import { useAuth } from '~/modules/root/context/AuthContext';
import { useFeature } from '~/modules/root';
import {
    useDownloadGate,
    type LookbookArtwork,
} from '~/modules-rocco/product/download-gate-state';

/**
 * Where "this browser has already recorded a download lead for that
 * professional" is kept. Shared so the revamped gate, which grants access from
 * its own success step, writes the same key {@link useGatedDownloads} reads.
 *
 * The key still reads `specsAccess` from when only Specs-tier files cost a
 * lead. Renaming it would re-gate every reader who has already paid the toll,
 * for no gain.
 */
export const downloadAccessStorageKey = (professionalID: number): string =>
    `specsAccess_${professionalID}`;

/**
 * What the lead is recorded against: whose file it was, which of their pages it
 * hung off, and where the reader was standing when they took it.
 */
export interface DownloadLeadContext {
    professionalID: number;
    /** Which of ArchiPro's download surfaces the file was taken from. */
    surface: DownloadSurface;
    /**
     * The library page the file hangs off: a product, a project, an article.
     * Absent for a file filed under the professional themselves.
     */
    libraryItemID?: number;
    /**
     * Path of the page the reader took it from, which the server turns into an
     * absolute URL on its own host. A path rather than a URL because the lead's
     * URL is read back into a Slack message and a Business Manager card.
     */
    pagePath?: string;
}

/**
 * The page the reader is standing on, query string included.
 *
 * The query is where a product page says which variant was being looked at,
 * and a tab says which page of a filtered list — the difference between a lead
 * that names what was on screen and one that names only the route. The server
 * keeps an allowlisted handful of those parameters and drops the rest
 * (LeadSourceUrl::sanitise), so sending the whole query is safe: it decides
 * what survives, not the browser. No hash — it never identifies the item, and
 * the sanitiser discards it anyway.
 */
const currentPagePath = (): string =>
    `${window.location.pathname}${window.location.search}`;

/**
 * Records the lead a download earns and remembers the access it buys, for a
 * download completed outside {@link useGatedDownloads} — the gate modal
 * resumes on its own success step, by which point the hook that started the
 * attempt may be unmounted with the modal that hosted it.
 */
export const grantDownloadAccess = (context: DownloadLeadContext): void => {
    void requestDownloadAccessMutation(context)
        .then(() => rememberDownloadAccess(context.professionalID))
        .catch(() => undefined);
};

/**
 * Remembers that this browser has already cost the professional a lead, so
 * their files stop asking. Written only once the lead has landed: granting it
 * first meant a failed write left the reader unasked and the client with
 * nothing.
 */
const rememberDownloadAccess = (professionalID: number): void => {
    try {
        window.localStorage.setItem(
            downloadAccessStorageKey(professionalID),
            JSON.stringify(true),
        );
    } catch {
        // Storage full / blocked. The lead has landed either way; the reader
        // is only asked again on a later visit.
    }
};

/**
 * Sends the requestDownloadAccess mutation via plain fetch (no Apollo needed).
 *
 * `libraryItemID` is optional because a file can hang off the professional
 * rather than off one of their pages — the profile's own downloads. The
 * mutation takes a nullable item and records the lead against the professional
 * alone, which is also why the surface is worth sending: it is the only thing
 * naming where such a lead came from.
 */
export const requestDownloadAccessMutation = ({
    professionalID,
    surface,
    libraryItemID,
    pagePath,
}: DownloadLeadContext): Promise<void> =>
    fetch('/graphql', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            query: `mutation RequestDownloadAccess($ProfessionalID: Int!, $LibraryItemID: Int, $Surface: DownloadSurface, $PagePath: String) {
                requestDownloadAccess(ProfessionalID: $ProfessionalID, LibraryItemID: $LibraryItemID, Surface: $Surface, PagePath: $PagePath) {
                    ... on Contact { ID }
                }
            }`,
            variables: {
                ProfessionalID: professionalID,
                LibraryItemID: libraryItemID ?? null,
                Surface: surface,
                PagePath: pagePath ?? currentPagePath(),
            },
        }),
    }).then(async (response) => {
        // A resolved fetch is not a recorded lead. A 500, a proxy's error
        // page, or a GraphQL error carried inside a 200 all settle happily,
        // and the caller reads that as the professional having been told.
        if (!response.ok) {
            throw new Error(
                `requestDownloadAccess responded ${response.status}`,
            );
        }

        const payload = (await response.json()) as { errors?: unknown[] };
        if (payload.errors?.length) {
            throw new Error('requestDownloadAccess returned errors');
        }
    });

/**
 * Which key the page a file hangs off is reported under **for GA4 and the ad
 * trackers**. `LoggingService::logEvent` does not flatten these — it reads
 * `LibraryItem`, `LibraryItemID`, `libraryPage`, `ProfessionalID`, `Source`,
 * `ExtraData`, `ExternalURL`, `ElementID`, `Filter` and the UTM pair, and drops
 * everything else. The payload below therefore sends `LibraryItemID` and
 * `ProfessionalID` as well, or a gated download reaches the warehouse with no
 * item and no professional on it.
 */
const itemKeyForSurface = (
    surface: DownloadSurface,
): 'articleID' | 'projectID' | 'productID' => {
    switch (surface) {
        case DownloadSurface.ArticleFiles:
            return 'articleID';
        case DownloadSurface.ProjectLookbook:
            return 'projectID';
        default:
            return 'productID';
    }
};

/** What a download click reports, wherever the click was taken. */
export interface DownloadsClickContext {
    /** The file's address, which the event carries as `fileUrl`. */
    link: string;
    /** Which of ArchiPro's download surfaces handed it over. */
    surface: DownloadSurface;
    /** The file's own name. Falls back to the surface when nobody knows it. */
    fileTitle?: string;
    fileID?: number;
    professionalID?: number;
    professionalTitle?: string;
    /** The library page the file hangs off, reported under its own key. */
    libraryItemID?: number;
    /** 0 for a reader with no account. */
    memberID: number;
}

/**
 * The `DownloadsClick` event, built in one place so every path that hands a
 * file over reports the same shape — the hook's own download, and the gate's
 * resume on the far side of auth.
 */
export const downloadsClickPayload = ({
    link,
    surface,
    fileTitle,
    fileID,
    professionalID,
    professionalTitle,
    libraryItemID,
    memberID,
}: DownloadsClickContext) => {
    const title = fileTitle || surface;
    return {
        url: new URL(window.location.href),
        ...(professionalTitle && { label: professionalTitle }),
        // Top-level, not inside `data`: this is the field that credits the
        // conversion to the professional whose ad brought the visitor. The row
        // this builder replaced passed it, and dropping it reported every
        // gated download as a site-wide conversion belonging to nobody.
        ...(professionalID && { professionalId: professionalID }),
        data: {
            ExtraData: buildDownloadsClickExtraData({
                fileTitle: title,
                fileUrl: link,
            }),
            // The two the warehouse actually keeps. Without them a gated
            // download lands unattributed, which is the same defect this
            // branch fixes on the Analytics Amplifier.
            ...(professionalID && { ProfessionalID: professionalID }),
            ...(libraryItemID && { LibraryItemID: libraryItemID }),
            // The lower-case pair below is GA4 and the ad trackers only.
            ...(professionalID && { professionalID }),
            ...(libraryItemID && {
                [itemKeyForSurface(surface)]: libraryItemID,
            }),
            ...(fileID && { fileID }),
            fileTitle: title,
            memberID,
            Source: surface,
        },
    };
};

interface UseGatedDownloadsOptions {
    /**
     * Which of ArchiPro's download surfaces this is. Every download writes the
     * same lead, so this is what tells the client which of their files earned
     * it. A caller with more than one kind of file names the other on the
     * attempt itself.
     */
    surface: DownloadSurface;
    /**
     * The library page the file hangs off: a product, or a project. Omitted
     * for a file that hangs off the professional itself — a profile download,
     * which belongs to no page.
     */
    libraryItemID?: number;
    /** The professional's ID. */
    professionalID: number;
    /** Hero of the item being downloaded, for the gate's brand pane. */
    itemImage?: string;
    /** The lookbook's own artwork, for a caller whose file has some. */
    lookbookArtwork?: LookbookArtwork;
    /** Called before the auth modal opens (e.g. close a hosting modal). */
    onBeforeAuth?: () => void;
    /** Called when the user cancels the auth modal. */
    onAuthCancel?: () => void;
}

/**
 * Download gating, on one rule for every file ArchiPro hands over: product
 * files and the tear sheet, the project lookbook, and a professional's own
 * profile downloads.
 *
 * Nothing opens for a signed-out reader. Taking a file creates a Lead for the
 * professional who published it — once per professional per browser, so a
 * reader working through a supplier's library is one lead rather than twelve —
 * and from then on that professional's files are plain links again.
 *
 * Files are no longer graded: the Safety / Specs / ProDesign tiers decided who
 * paid what, and every file now costs the same.
 *
 * A file with no professional behind it — an ArchiPro editorial article's own
 * files — still asks for an account, but there is nobody for the lead to
 * belong to, so none is recorded and signing in is the whole price.
 */
export const useGatedDownloads = ({
    surface,
    libraryItemID,
    professionalID,
    itemImage,
    lookbookArtwork,
    onBeforeAuth,
    onAuthCancel,
}: UseGatedDownloadsOptions) => {
    const user = useUser();
    const isLoggedIn = isUserLoggedIn(user);
    const { showAuthModal, hideAuthModal } = useAuth();
    const tracker = useTracker();
    const gateEnabled = useFeature('revamp_2026');
    const { open: openDownloadGate } = useDownloadGate();

    const [hasDownloadAccess, setHasDownloadAccess] =
        useLocalStorageState<boolean>(
            downloadAccessStorageKey(professionalID),
            {
                defaultValue: false,
            },
        );

    const hasCreatedLead = useRef(false);
    const [leadConfirmed, setLeadConfirmed] = useState(false);

    /** Nobody to record a lead for, so the gate is the sign-in wall alone. */
    const leadable = professionalID > 0;

    /**
     * Whether a file still has to go through the gate: the reader is signed
     * out, or signed in but has not yet been counted as a lead for this
     * professional.
     */
    const isGated = !isLoggedIn || (leadable && !hasDownloadAccess);

    /** Creates a lead on the first gated download, then persists access for future visits. */
    const createLeadIfNeeded = useCallback(
        (takenFrom: DownloadSurface) => {
            if (!leadable || hasCreatedLead.current) return;
            // Claimed before the round-trip, so a reader taking three files in
            // a row files one lead rather than three.
            hasCreatedLead.current = true;

            requestDownloadAccessMutation({
                professionalID,
                surface: takenFrom,
                libraryItemID,
            })
                .then(() => {
                    setHasDownloadAccess(true);
                    setLeadConfirmed(true);
                })
                .catch(() => {
                    // The client never heard about this download, so the
                    // reader has not paid for the next one: let the attempt
                    // after this one try again.
                    hasCreatedLead.current = false;
                });
        },
        [leadable, professionalID, libraryItemID, setHasDownloadAccess],
    );

    /**
     * Raises the lead a file earns without handing the file over.
     *
     * Saving a file to a design board reaches it as surely as downloading it,
     * so the professional is owed the lead either way. Defaults to this
     * surface, the way an attempt does.
     */
    const registerDownloadLead = useCallback(
        (takenFrom: DownloadSurface = surface) => {
            createLeadIfNeeded(takenFrom);
        },
        [createLeadIfNeeded, surface],
    );

    /**
     * Handles a gated file download: the sign-in wall when the reader has no
     * account, then the file, then the lead it earns.
     */
    const handleGatedDownload = useCallback(
        (
            link: string,
            takenFrom: DownloadSurface = surface,
            /**
             * What the caller knows about the file. The hook only has the
             * surface and the link, and a row that knows its title and id
             * should not have to log the event itself to say so — two loggers
             * for one click is how this ended up double-counting.
             */
            file?: { title?: string; id?: number; professionalTitle?: string },
        ) => {
            const doDownload = () => {
                // The same event the plain download anchors report. This path
                // serves every gated file and every generated document, and it
                // reported nothing — so a download taken through the gate, which
                // is what every logged-out reader takes, was invisible while the
                // ungated one next to it was counted.
                void tracker.log(
                    'DownloadsClick',
                    downloadsClickPayload({
                        link,
                        surface: takenFrom,
                        fileTitle: file?.title,
                        fileID: file?.id,
                        professionalID,
                        professionalTitle: file?.professionalTitle,
                        libraryItemID,
                        memberID: isLoggedIn ? user?.ID || 0 : 0,
                    }),
                );
                window.open(link, '_blank', 'noopener');
                createLeadIfNeeded(takenFrom);
            };

            if (!isLoggedIn) {
                onBeforeAuth?.();
                if (gateEnabled) {
                    // The revamped gate carries the attempt through auth and
                    // hands the file back on the far side, so the reader does
                    // not have to find their way back to it.
                    openDownloadGate({
                        action: 'download',
                        link,
                        surface: takenFrom,
                        // Carried so the resume on the far side of auth can
                        // report the file by name rather than by surface.
                        ...(file?.title && { fileName: file.title }),
                        ...(file?.id && { fileID: file.id }),
                        ...(file?.professionalTitle && {
                            professionalTitle: file.professionalTitle,
                        }),
                        // The page the reader was standing on when they asked,
                        // which is not where the gate hands the file back: the
                        // social round-trip lands them on a callback route.
                        pagePath: currentPagePath(),
                        libraryItemID,
                        professionalID,
                        itemImage,
                        lookbookArtwork,
                    });
                    return;
                }
                showAuthModal({
                    authSource: 'productFiles',
                    skipPostSignUp: true,
                    redirect: `${window.location.pathname}${window.location.search}${window.location.hash}`,
                    header: 'Sign in to download',
                    subtitle:
                        'Create a free account to download product files and resources.',
                    onAuthSuccess: () => {
                        hideAuthModal();
                    },
                    onCancel: () => {
                        hideAuthModal();
                        onAuthCancel?.();
                    },
                });
                return;
            }

            doDownload();
        },
        [
            isLoggedIn,
            gateEnabled,
            openDownloadGate,
            surface,
            libraryItemID,
            professionalID,
            itemImage,
            tracker,
            user,
            lookbookArtwork,
            showAuthModal,
            hideAuthModal,
            createLeadIfNeeded,
            onBeforeAuth,
            onAuthCancel,
        ],
    );

    return {
        isLoggedIn,
        isGated,
        leadConfirmed,
        handleGatedDownload,
        registerDownloadLead,
    };
};
